import React, { useState, useEffect, useRef, useCallback, useReducer, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import './GameScreen.css';
import { fetchNewChallenge, submitPosition, finalizeGameSession, fetchMapData, fetchUserSettings, fetchSounds, clearApiCache } from '../services/apiService';
import { useFocusTrap } from '../hooks';
import ReportModal from './ReportModal';
import Advertisement from './Advertisement';
import confetti from 'canvas-confetti';

// ===== REDUCERS =====

// Zoom state reducer - groups zoom, pan, and drag states
const zoomReducer = (state, action) => {
  switch (action.type) {
    case 'SET_ZOOM':
      return { ...state, zoomLevel: action.payload };
    case 'SET_PAN':
      return { ...state, panPosition: action.payload };
    case 'START_DRAG':
      return { ...state, isDragging: true, dragStart: action.payload };
    case 'UPDATE_DRAG':
      return {
        ...state,
        dragStart: action.payload.dragStart,
        panPosition: action.payload.panPosition
      };
    case 'END_DRAG':
      return { ...state, isDragging: false };
    case 'SET_MAP_FOCUSED':
      return { ...state, isMapFocused: action.payload };
    case 'RESET':
      return { zoomLevel: 1, panPosition: { x: 0, y: 0 }, isDragging: false, dragStart: { x: 0, y: 0 }, isMapFocused: false };
    default:
      return state;
  }
};

// Modal state reducer - groups all modal-related states
const modalReducer = (state, action) => {
  switch (action.type) {
    case 'OPEN_MAP_MODAL':
      return { ...state, showMapModal: true, tempSelectedMap: action.payload };
    case 'CLOSE_MAP_MODAL':
      return { ...state, showMapModal: false, tempSelectedMap: '' };
    case 'SHOW_ERROR':
      return { ...state, showErrorModal: true, errorMessage: action.payload.message, errorCallback: action.payload.callback };
    case 'CLOSE_ERROR':
      return { ...state, showErrorModal: false, errorMessage: '', errorCallback: null };
    case 'SHOW_FINAL_SCORE':
      return { ...state, showFinalScoreModal: true };
    case 'CLOSE_FINAL_SCORE':
      return { ...state, showFinalScoreModal: false };
    default:
      return state;
  }
};

// Loading flags reducer - groups all loading state flags
const loadingFlagsReducer = (state, action) => {
  switch (action.type) {
    case 'SET_USER_SETTINGS_LOADED':
      return { ...state, userSettings: true };
    case 'SET_SOUNDS_LOADED':
      return { ...state, sounds: true };
    case 'SET_CHALLENGE_LOADED':
      return { ...state, challenge: action.payload };
    case 'SET_MAP_DATA_LOADED':
      return { ...state, mapData: true };
    case 'RESET_ALL':
      return { userSettings: false, sounds: false, challenge: false, mapData: false };
    default:
      return state;
  }
};

// Initial states
const initialZoomState = { zoomLevel: 1, panPosition: { x: 0, y: 0 }, isDragging: false, dragStart: { x: 0, y: 0 }, isMapFocused: false };
const initialModalState = { showMapModal: false, tempSelectedMap: '', showErrorModal: false, errorMessage: '', errorCallback: null, showFinalScoreModal: false };
const initialLoadingFlags = { userSettings: false, sounds: false, challenge: false, mapData: false };

const GameScreen = ({ gameState, updateGameState, onGameEnd, user, globalMuted, setGlobalMuted, globalVolume, setGlobalVolume }) => {
  const { t } = useTranslation();

  // ===== REDUCERS =====
  const [zoomState, zoomDispatch] = useReducer(zoomReducer, initialZoomState);
  const [modalState, modalDispatch] = useReducer(modalReducer, initialModalState);
  const [loadingFlags, loadingDispatch] = useReducer(loadingFlagsReducer, initialLoadingFlags);

  // Destructure for easier access
  const { zoomLevel, panPosition, isDragging, dragStart, isMapFocused } = zoomState;
  const { showMapModal, tempSelectedMap, showErrorModal, errorMessage, errorCallback, showFinalScoreModal } = modalState;

  // ===== SIMPLE STATES =====
  // Game state
  const [loading, setLoading] = useState(true);
  const [selectedMap, setSelectedMap] = useState('');
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [selectedPositionRelative, setSelectedPositionRelative] = useState(null);
  const [roundResult, setRoundResult] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(60);
  const [mapData, setMapData] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [timeExpired, setTimeExpired] = useState(false);
  const [timerAlertPlayed, setTimerAlertPlayed] = useState(false);

  // Sound state
  const [selectedTimerAlert, setSelectedTimerAlert] = useState(null);
  const [disableSoundAlerts, setDisableSoundAlerts] = useState(false);
  const [sounds, setSounds] = useState([]);

  // Layout preference
  const [gameLayout, setGameLayout] = useState(user?.game_layout || 'split');

  // Report modal state
  const [showReportModal, setShowReportModal] = useState(false);

  // ===== MEMOIZED VALUES =====
  // Total score calculation - used in multiple places
  const totalScore = useMemo(() => {
    return gameState.scores.reduce((sum, score) => sum + score, 0);
  }, [gameState.scores]);

  // Map keys for iteration
  const mapKeys = useMemo(() => Object.keys(mapData), [mapData]);

  // Reset zoom and pan helper
  const resetZoomAndPan = useCallback(() => {
    zoomDispatch({ type: 'RESET' });
  }, []);

  // Wrapper for onGameEnd
  const handleGameEnd = useCallback(() => {
    // Reset all loading flags to ensure fresh data on next game start
    loadingDispatch({ type: 'RESET_ALL' });

    // Clear API cache to ensure fresh data on next game start
    clearApiCache();

    // Call the original onGameEnd function
    onGameEnd();
  }, [onGameEnd]);

  // Confetti celebration for high scores
  const triggerConfetti = useCallback((score) => {
    if (score >= 4500) {
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 },
        colors: ['#ff9c00', '#ffb84d', '#ffd700', '#ff6b00', '#ffffff']
      });
    } else if (score >= 3000) {
      // Smaller celebration for good scores
      confetti({
        particleCount: 50,
        spread: 45,
        origin: { y: 0.6 },
        colors: ['#ff9c00', '#ffb84d']
      });
    }
  }, []);

  // Refs for DOM elements
  const timerRef = useRef(null);
  // mapContainerRef is not used in this component
  const modalMapContainerRef = useRef(null);
  const selectedMapContainerRef = useRef(null);
  const modalMapImageRef = useRef(null);
  const selectedMapImageRef = useRef(null);
  const modalMapOverlayRef = useRef(null);
  const selectedMapOverlayRef = useRef(null);
  const timerAlertAudioRef = useRef(null);

  // Focus trap refs for modals (accessibility)
  const mapModalFocusTrapRef = useFocusTrap(showMapModal, {
    onEscape: () => modalDispatch({ type: 'CLOSE_MAP_MODAL' }),
    autoFocus: true,
    restoreFocus: true
  });

  const errorModalFocusTrapRef = useFocusTrap(showErrorModal, {
    onEscape: () => modalDispatch({ type: 'CLOSE_ERROR' }),
    autoFocus: true,
    restoreFocus: true
  });

  const finalScoreModalFocusTrapRef = useFocusTrap(showFinalScoreModal, {
    onEscape: handleGameEnd,
    autoFocus: true,
    restoreFocus: true
  });

  // Convert relative position (0-1) to absolute position (pixels)
  const relativeToAbsolutePosition = useCallback((relativePos, containerRef, imageRef, mapId = null) => {
    if (!relativePos || !containerRef.current || !imageRef?.current) return null;

    const containerRect = containerRef.current.getBoundingClientRect();

    // Calculate the actual image dimensions within the container
    // When using object-fit: contain, the image maintains its aspect ratio
    // and may have empty space on the sides or top/bottom
    // Use mapId parameter if provided, otherwise fall back to selectedMap
    const effectiveMapId = mapId || selectedMap;

    // Prefer natural image dimensions if available, fallback to mapData
    const imgElement = imageRef.current;
    const mapWidth = imgElement?.naturalWidth || mapData[effectiveMapId]?.width || 100;
    const mapHeight = imgElement?.naturalHeight || mapData[effectiveMapId]?.height || 100;
    const imageAspectRatio = mapWidth / mapHeight;
    const containerAspectRatio = containerRect.width / containerRect.height;

    let actualImageWidth, actualImageHeight;
    let offsetX = 0, offsetY = 0;

    if (imageAspectRatio > containerAspectRatio) {
      // Image is wider than container (empty space on top and bottom)
      actualImageWidth = containerRect.width;
      actualImageHeight = containerRect.width / imageAspectRatio;
      offsetY = (containerRect.height - actualImageHeight) / 2;
    } else {
      // Image is taller than container (empty space on sides)
      actualImageHeight = containerRect.height;
      actualImageWidth = containerRect.height * imageAspectRatio;
      offsetX = (containerRect.width - actualImageWidth) / 2;
    }

    // Ensure relativePos is within the range [0, 1] to keep the dot within the image boundaries
    const clampedRelativePos = {
      x: Math.max(0, Math.min(1, relativePos.x)),
      y: Math.max(0, Math.min(1, relativePos.y))
    };

    // Calculate position relative to the actual image dimensions
    return {
      x: offsetX + (clampedRelativePos.x * actualImageWidth),
      y: offsetY + (clampedRelativePos.y * actualImageHeight)
    };
  }, [mapData, selectedMap]);

  // Convert absolute position (pixels) to relative position (0-1)
  // This function is defined but not currently used in the component
  // Keeping it commented out for potential future use
  /*
  const absoluteToRelativePosition = useCallback((absolutePos, containerRef, imageRef) => {
    if (!absolutePos || !containerRef.current || !imageRef?.current) return null;

    const containerRect = containerRef.current.getBoundingClientRect();

    // Calculate the actual image dimensions within the container
    const imageAspectRatio = mapData[selectedMap]?.width / mapData[selectedMap]?.height || 1;
    const containerAspectRatio = containerRect.width / containerRect.height;

    let actualImageWidth, actualImageHeight;
    let offsetX = 0, offsetY = 0;

    if (imageAspectRatio > containerAspectRatio) {
      // Image is wider than container (empty space on top and bottom)
      actualImageWidth = containerRect.width;
      actualImageHeight = containerRect.width / imageAspectRatio;
      offsetY = (containerRect.height - actualImageHeight) / 2;
    } else {
      // Image is taller than container (empty space on sides)
      actualImageHeight = containerRect.height;
      actualImageWidth = containerRect.height * imageAspectRatio;
      offsetX = (containerRect.width - actualImageWidth) / 2;
    }

    // Calculate position relative to the actual image dimensions
    const relativePos = {
      x: (absolutePos.x - offsetX) / actualImageWidth,
      y: (absolutePos.y - offsetY) / actualImageHeight
    };

    // Ensure the relative position is within the range [0, 1]
    return {
      x: Math.max(0, Math.min(1, relativePos.x)),
      y: Math.max(0, Math.min(1, relativePos.y))
    };
  }, [mapData, selectedMap]);
  */


  // Function to recalculate positions based on current state
  const recalculatePositions = useCallback(() => {
    // Only recalculate if we're showing results (map with points)
    if (showResults && selectedMapContainerRef.current && selectedMapImageRef.current) {
      // Recalculate selected position
      if (selectedPositionRelative) {
        const newAbsolutePos = relativeToAbsolutePosition(selectedPositionRelative, selectedMapContainerRef, selectedMapImageRef);
        if (newAbsolutePos && 
            (!selectedPosition || 
             selectedPosition.x !== newAbsolutePos.x || 
             selectedPosition.y !== newAbsolutePos.y)) {
          setSelectedPosition(newAbsolutePos);
        }
      }

    }
    // Handle position in modal
    else if (showMapModal && selectedPositionRelative && modalMapContainerRef.current && modalMapImageRef.current) {
      const newAbsolutePos = relativeToAbsolutePosition(selectedPositionRelative, modalMapContainerRef, modalMapImageRef, tempSelectedMap);
      if (newAbsolutePos && 
          (!selectedPosition || 
           selectedPosition.x !== newAbsolutePos.x || 
           selectedPosition.y !== newAbsolutePos.y)) {
        setSelectedPosition(newAbsolutePos);
      }
    }
  }, [
    showResults,
    selectedPositionRelative,
    relativeToAbsolutePosition,
    showMapModal,
    selectedPosition,
    tempSelectedMap
  ]);

  // Load map data when component mounts
  useEffect(() => {
    const loadMapData = async () => {
      if (loadingFlags.mapData) return;

      try {
        const maps = await fetchMapData();
        setMapData(maps);
        loadingDispatch({ type: 'SET_MAP_DATA_LOADED' });
      } catch (error) {
        console.error('Error loading map data:', error);
      }
    };

    loadMapData();
  }, [loadingFlags.mapData]);

  // Load user settings when component mounts
  useEffect(() => {
    const loadUserSettings = async () => {
      if (loadingFlags.userSettings) return;

      try {
        const settings = await fetchUserSettings();
        setSelectedTimerAlert(settings.selected_timer_alert || null);
        setDisableSoundAlerts(settings.disable_sound_alerts || false);
        setGameLayout(settings.game_layout || 'split');
        loadingDispatch({ type: 'SET_USER_SETTINGS_LOADED' });
      } catch (error) {
        console.error('Error loading user settings:', error);
      }
    };

    loadUserSettings();
  }, [loadingFlags.userSettings]);

  // Load sounds when component mounts
  useEffect(() => {
    const loadSounds = async () => {
      if (loadingFlags.sounds) return;

      try {
        const soundsData = await fetchSounds();
        setSounds(soundsData);
        loadingDispatch({ type: 'SET_SOUNDS_LOADED' });
      } catch (error) {
        console.error('Error loading sounds:', error);
      }
    };

    loadSounds();
  }, [loadingFlags.sounds]);

  // Load challenge when component mounts or round changes
  // Both normal and daily challenge modes use the same /api/challenge endpoint.
  // Daily challenges pass dailyChallengeId so the server returns predetermined points.
  useEffect(() => {
    const loadChallenge = async () => {
      if (loadingFlags.challenge && gameState.challengeId) return;

      setLoading(true);
      try {
        const challenge = await fetchNewChallenge(
          gameState.difficulty,
          gameState.sessionId,
          gameState.gamemode,
          gameState.dailyChallengeId || null
        );

        updateGameState({
          challengeId: challenge.id,
          challengeImage: challenge.image,
          sessionId: challenge.sessionId,
          currentPointId: challenge.pointId,
          currentMapId: challenge.mapId
        });

        loadingDispatch({ type: 'SET_CHALLENGE_LOADED', payload: true });
        setLoading(false);
      } catch (error) {
        console.error('Error loading challenge:', error);

        if (error.response && error.response.status === 403 &&
            error.response.data && error.response.data.error === 'daily_limit_reached') {
          showError(t('game.dailyLimitReached', 'You have reached the daily limit of games. Register with BattleNet to play unlimited games!'), onGameEnd);
        }

        setLoading(false);
      }
    };

    if (gameState.currentRound > 0 && !gameState.challengeId) {
      loadChallenge();
    }
  }, [gameState.currentRound, gameState.difficulty, gameState.sessionId, gameState.gamemode, updateGameState, gameState.challengeId, onGameEnd, t, loadingFlags.challenge, gameState.dailyChallengeId]);

  // Handle window resize to recalculate positions with debounce
  useEffect(() => {
    // Debounce function to prevent too many recalculations during resize
    let resizeTimer;
    const handleResize = () => {
      // Only recalculate if we're showing results or the map modal
      if (showResults || showMapModal) {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
          recalculatePositions();
        }, 100); // 100ms debounce
      }
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(resizeTimer);
    };
  }, [recalculatePositions, showResults, showMapModal]);

  // Recalculate positions when showResults or showMapModal changes
  useEffect(() => {
    // Only recalculate if we're showing results or the map modal
    // This prevents unnecessary calculations when neither is active
    if (showResults || showMapModal) {
      // Use setTimeout to ensure the DOM has updated before recalculating
      const timer = setTimeout(() => {
        recalculatePositions();
      }, 0);

      return () => clearTimeout(timer);
    } else {
      // Reset zoom and pan when not showing results or map modal
      resetZoomAndPan();
    }
  }, [showResults, showMapModal, recalculatePositions, resetZoomAndPan]);


  // Function to handle time expiration - defined outside useEffect to avoid circular dependency
  const handleTimeExpiration = useCallback(() => {
    // Clear the interval
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    // Close map modal if it's open
    if (showMapModal) {
      modalDispatch({ type: 'CLOSE_MAP_MODAL' });
    }

    // We'll set a flag to indicate time expiration
    // The actual submission will happen in the handleSubmit function
    setTimeExpired(true);

    // Submit with default values
    const defaultMap = mapKeys[0] || '';
    const defaultPosition = { x: 0.5, y: 0.5 }; // Center in 0-1 range

    // Update state for UI consistency
    setSelectedMap(defaultMap);
    setSelectedPositionRelative(defaultPosition);

    // Trigger submission with the default map
    if (defaultMap) {
      // Get map dimensions from mapData
      const mapWidth = mapData[defaultMap]?.width || 100;
      const mapHeight = mapData[defaultMap]?.height || 100;

      // Convert relative coordinates to absolute pixel coordinates
      const absolutePosition = { 
        x: defaultPosition.x * mapWidth, 
        y: defaultPosition.y * mapHeight 
      };

      // Submit the position directly to the API (time expired, so timeRemaining is 0)
      submitPosition(
        gameState.challengeId,
        defaultMap,
        absolutePosition,
        gameState.sessionId,
        0
      ).then(result => {
        // Create a result object with 0 points
        const timeExpiredResult = {
          score: 0,
          distance: -1, // -1 to hide the line and actual position
          timeExpired: true
        };

        // Update scores in game state with 0 points
        const updatedScores = [...gameState.scores, 0];
        updateGameState({ scores: updatedScores });

        // Set round result and show results
        setRoundResult(timeExpiredResult);
        setShowResults(true);
      }).catch(error => {
        console.error('Error submitting position after time expired:', error);
        showError(t('game.errorSubmittingPosition', 'Error submitting position. Please try again.'));
      });
    }

    return 0;
  }, [
    timerRef, 
    showMapModal, 
    mapData, 
    gameState.challengeId, 
    gameState.sessionId, 
    gameState.scores, 
    t, 
    updateGameState
  ]);

  // Set up timer
  useEffect(() => {
    // Don't start timer if loading, showing results, or no map data available
    if (loading || showResults || mapKeys.length === 0) return;

    // Reset timer alert played state when timer starts
    setTimerAlertPlayed(false);

    // Function to play timer alert sound
    const playTimerAlert = () => {
      // Stop any previous audio before playing new one
      if (timerAlertAudioRef.current) {
        timerAlertAudioRef.current.pause();
        timerAlertAudioRef.current = null;
      }

      // If sound alerts are disabled or session is muted, don't play any sound
      if (disableSoundAlerts || globalMuted) {
        setTimerAlertPlayed(true);
        return;
      }

      try {
        let audio;

        // Use selected timer alert sound if available, otherwise use default
        if (selectedTimerAlert) {
          const sound = sounds.find(s => s.code === selectedTimerAlert);
          if (sound) {
            audio = new Audio(sound.path);
          }
        }

        // Fallback to default sound if no selection or selected sound not found
        if (!audio) {
          audio = new Audio('/sounds/timer_alert.mp3');
        }

        audio.volume = globalVolume / 100; // Set volume based on globalVolume (0-100 to 0-1)
        timerAlertAudioRef.current = audio; // Store reference for later control
        audio.play().catch(error => {
          console.error('Error playing timer alert sound:', error);
        });
        setTimerAlertPlayed(true);
      } catch (error) {
        console.error('Error creating timer alert sound:', error);
      }
    };


    // Set up the interval for the timer
    timerRef.current = setInterval(() => {
      setTimeRemaining(prev => {
        // When timer reaches 10 seconds
        if (prev === 10) {
          // Play timer alert sound
          playTimerAlert();
        }

        // When timer reaches 0
        if (prev <= 1) {
          return handleTimeExpiration();
        }

        // Normal countdown
        return prev - 1;
      });
    }, 1000);

    // Cleanup function
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      // Stop audio if playing
      if (timerAlertAudioRef.current) {
        timerAlertAudioRef.current.pause();
        timerAlertAudioRef.current = null;
      }
    };
  }, [loading, showResults, mapData, showMapModal, sounds, handleTimeExpiration, selectedTimerAlert, disableSoundAlerts, globalMuted, globalVolume]);

  // Stop timer alert audio when muted
  useEffect(() => {
    if (globalMuted && timerAlertAudioRef.current) {
      timerAlertAudioRef.current.pause();
      timerAlertAudioRef.current = null;
    }
  }, [globalMuted]);

  // Handle zoom on wheel event
  const handleWheel = useCallback((e) => {
    // Only prevent default and apply zoom when map is focused (user clicked on it)
    if (!isMapFocused) return;

    e.preventDefault();

    // Disable wheel zoom when showing results (only allow button zoom)
    if (showResults) return;

    // Determine zoom direction
    const zoomDirection = e.deltaY < 0 ? 1 : -1;
    const zoomFactor = 0.1;
    const newZoomLevel = Math.max(1, Math.min(5, zoomLevel + (zoomDirection * zoomFactor)));

    if (newZoomLevel !== zoomLevel) {
      zoomDispatch({ type: 'SET_ZOOM', payload: newZoomLevel });
    }
  }, [showResults, zoomLevel, isMapFocused]);

  // Attach wheel event listeners with { passive: false } to allow preventDefault
  useEffect(() => {
    const selectedContainer = selectedMapContainerRef.current;
    const modalContainer = modalMapContainerRef.current;

    if (selectedContainer) {
      selectedContainer.addEventListener('wheel', handleWheel, { passive: false });
    }
    if (modalContainer) {
      modalContainer.addEventListener('wheel', handleWheel, { passive: false });
    }

    return () => {
      if (selectedContainer) {
        selectedContainer.removeEventListener('wheel', handleWheel);
      }
      if (modalContainer) {
        modalContainer.removeEventListener('wheel', handleWheel);
      }
    };
  }, [handleWheel]);

  // Handle mouse down for panning
  const handleMouseDown = useCallback((e) => {
    if (zoomLevel > 1) {
      zoomDispatch({ type: 'START_DRAG', payload: { x: e.clientX, y: e.clientY } });
    }
  }, [zoomLevel]);

  // Handle mouse move for panning
  const handleMouseMove = useCallback((e) => {
    if (isDragging && zoomLevel > 1) {
      const deltaX = e.clientX - dragStart.x;
      const deltaY = e.clientY - dragStart.y;

      zoomDispatch({
        type: 'UPDATE_DRAG',
        payload: {
          dragStart: { x: e.clientX, y: e.clientY },
          panPosition: { x: panPosition.x + deltaX, y: panPosition.y + deltaY }
        }
      });
    }
  }, [isDragging, zoomLevel, dragStart, panPosition]);

  // Handle mouse up to stop panning
  const handleMouseUp = useCallback(() => {
    zoomDispatch({ type: 'END_DRAG' });
  }, []);

  // Handle click outside map to deactivate zoom
  useEffect(() => {
    const handleClickOutside = (e) => {
      const selectedContainer = selectedMapContainerRef.current;
      const modalContainer = modalMapContainerRef.current;

      // Check if click is outside both map containers
      const isOutsideSelected = selectedContainer && !selectedContainer.contains(e.target);
      const isOutsideModal = modalContainer && !modalContainer.contains(e.target);

      // If we have a modal open, only check modal container
      if (showMapModal) {
        if (isOutsideModal && isMapFocused) {
          zoomDispatch({ type: 'SET_MAP_FOCUSED', payload: false });
        }
      } else {
        if (isOutsideSelected && isMapFocused) {
          zoomDispatch({ type: 'SET_MAP_FOCUSED', payload: false });
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMapFocused, showMapModal]);

  // Handle map selection
  const handleMapSelect = useCallback((mapId) => {
    setSelectedPosition(null);
    resetZoomAndPan();
    modalDispatch({ type: 'OPEN_MAP_MODAL', payload: mapId });
  }, [resetZoomAndPan]);

  // Handle modal cancellation
  const handleModalCancel = useCallback(() => {
    modalDispatch({ type: 'CLOSE_MAP_MODAL' });
    resetZoomAndPan();
  }, [resetZoomAndPan]);

  // Handle modal confirmation
  const handleModalConfirm = () => {
    if (!tempSelectedMap) return;

    let relativePositionToUse = selectedPositionRelative;

    if (!relativePositionToUse) {
      relativePositionToUse = { x: 0.5, y: 0.5 };
      setSelectedPositionRelative(relativePositionToUse);
    }

    // Calculate absolute position from relative
    if (modalMapContainerRef.current && modalMapImageRef.current) {
      const containerRect = modalMapContainerRef.current.getBoundingClientRect();

      // Prefer natural image dimensions if available, fallback to mapData
      const imgElement = modalMapImageRef.current;
      const displayMapWidth = imgElement?.naturalWidth || mapData[tempSelectedMap]?.width || 100;
      const displayMapHeight = imgElement?.naturalHeight || mapData[tempSelectedMap]?.height || 100;

      const imageAspectRatio = displayMapWidth / displayMapHeight;
      const containerAspectRatio = containerRect.width / containerRect.height;

      let actualImageWidth, actualImageHeight;
      let offsetX = 0, offsetY = 0;

      if (imageAspectRatio > containerAspectRatio) {
        actualImageWidth = containerRect.width;
        actualImageHeight = containerRect.width / imageAspectRatio;
        offsetY = (containerRect.height - actualImageHeight) / 2;
      } else {
        actualImageHeight = containerRect.height;
        actualImageWidth = containerRect.height * imageAspectRatio;
        offsetX = (containerRect.width - actualImageWidth) / 2;
      }

      setSelectedPosition({
        x: offsetX + (relativePositionToUse.x * actualImageWidth),
        y: offsetY + (relativePositionToUse.y * actualImageHeight)
      });
    }

    resetZoomAndPan();
    setSelectedMap(tempSelectedMap);
    modalDispatch({ type: 'CLOSE_MAP_MODAL' });

    // Stop timer
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    // Prepare the position for submission
    // Get map dimensions from mapData
    const mapWidth = mapData[tempSelectedMap]?.width || 100;
    const mapHeight = mapData[tempSelectedMap]?.height || 100;

    // Convert relative coordinates (0-1 range) to absolute pixel coordinates
    const positionToSubmit = {
      x: relativePositionToUse.x * mapWidth,
      y: relativePositionToUse.y * mapHeight
    };

    // Submit the position directly to the API
    submitPosition(
      gameState.challengeId,
      tempSelectedMap,
      positionToSubmit,
      gameState.sessionId,
      timeRemaining
    ).then(result => {
      // Update scores in game state
      const updatedScores = [...gameState.scores, result.score];
      updateGameState({ scores: updatedScores });

      // Set round result and show results
      setRoundResult(result);
      setShowResults(true);

      // Trigger confetti for high scores
      triggerConfetti(result.score);
    }).catch(error => {
      console.error('Error submitting position:', error);
      showError(t('game.errorSubmittingPosition', 'Error submitting position. Please try again.'));
    });
  };

  // Handle position selection on map
  const handleMapClick = (e) => {
    // Activate map focus for scroll wheel zoom
    if (!isMapFocused) {
      zoomDispatch({ type: 'SET_MAP_FOCUSED', payload: true });
    }

    // Determine if we're in the modal or results view
    const isModal = showMapModal;
    const containerRef = isModal ? modalMapContainerRef : selectedMapContainerRef;
    const imageRef = isModal ? modalMapImageRef : selectedMapImageRef;
    const mapId = isModal ? tempSelectedMap : selectedMap;

    // Only proceed if we have valid refs and a selected map
    if (!containerRef.current || !imageRef.current || !mapId) return;

    // Get container dimensions
    const containerRect = containerRef.current.getBoundingClientRect();

    // Get map dimensions - prefer natural image dimensions if available, fallback to mapData
    const imgElement = imageRef.current;
    const mapWidth = imgElement?.naturalWidth || mapData[mapId]?.width || 100;
    const mapHeight = imgElement?.naturalHeight || mapData[mapId]?.height || 100;

    // Calculate aspect ratios
    const imageAspectRatio = mapWidth / mapHeight;
    const containerAspectRatio = containerRect.width / containerRect.height;

    // Calculate actual image dimensions and offsets within the container
    let actualImageWidth, actualImageHeight;
    let offsetX = 0, offsetY = 0;

    if (imageAspectRatio > containerAspectRatio) {
      // Image is wider than container (letterboxed - empty space on top and bottom)
      actualImageWidth = containerRect.width;
      actualImageHeight = containerRect.width / imageAspectRatio;
      offsetY = (containerRect.height - actualImageHeight) / 2;
    } else {
      // Image is taller than container (pillarboxed - empty space on sides)
      actualImageHeight = containerRect.height;
      actualImageWidth = containerRect.height * imageAspectRatio;
      offsetX = (containerRect.width - actualImageWidth) / 2;
    }

    // Calculate click position relative to the container
    const clickX = e.clientX - containerRect.left;
    const clickY = e.clientY - containerRect.top;

    // Add a small tolerance for edge clicks (helps with border-radius and minor calculation differences)
    const edgeTolerance = 5;

    // Check if click is within the actual image bounds (with tolerance)
    if (
      clickX >= offsetX - edgeTolerance &&
      clickX <= offsetX + actualImageWidth + edgeTolerance &&
      clickY >= offsetY - edgeTolerance &&
      clickY <= offsetY + actualImageHeight + edgeTolerance
    ) {
      // Adjust for zoom and pan
      // First, calculate the center of the container
      const centerX = containerRect.width / 2;
      const centerY = containerRect.height / 2;

      // Calculate the click position relative to the center
      const relativeToCenter = {
        x: clickX - centerX,
        y: clickY - centerY
      };

      // Adjust for zoom and pan
      const adjustedX = (relativeToCenter.x / zoomLevel) - (panPosition.x / zoomLevel) + centerX;
      const adjustedY = (relativeToCenter.y / zoomLevel) - (panPosition.y / zoomLevel) + centerY;

      // Calculate position relative to the actual image (removing offsets)
      const imageX = adjustedX - offsetX;
      const imageY = adjustedY - offsetY;

      // Calculate relative position (0-1 range)
      const relativePos = {
        x: imageX / actualImageWidth,
        y: imageY / actualImageHeight
      };

      // Ensure values are within 0-1 range
      const clampedRelativePos = {
        x: Math.max(0, Math.min(1, relativePos.x)),
        y: Math.max(0, Math.min(1, relativePos.y))
      };

      // Store the relative position
      setSelectedPositionRelative(clampedRelativePos);

      // Calculate the absolute position based on the relative position
      const absolutePos = relativeToAbsolutePosition(clampedRelativePos, containerRef, imageRef, mapId);
      setSelectedPosition(absolutePos);
    }
  };

  // Handle submit button click
  // This function is defined but not currently used in the component
  // Keeping it commented out for potential future use
  /*
  const handleSubmit = useCallback(async (mapOverride = null) => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    // Check if mapData is empty
    if (Object.keys(mapData).length === 0) {
      console.error('No map data available');
      // Try to load map data again
      try {
        const maps = await fetchMapData();
        setMapData(maps);

        // Check if we successfully loaded map data
        if (Object.keys(maps).length === 0) {
          console.error('Failed to load map data for submission');
          // Show an error message or return to main screen
          showError(t('game.errorLoadingMapData', 'Error loading map data. Please try again.'));
          return; // Don't proceed if no map data
        }
      } catch (error) {
        console.error('Error loading map data:', error);
        showError(t('game.errorLoadingMapData', 'Error loading map data. Please try again.'));
        return; // Don't proceed if error loading map data
      }
    }

    // Prepare the map and position values to use in the API call
    // Use overrides if provided, otherwise use state values
    let mapToSubmit = mapOverride !== null ? mapOverride : selectedMap;
    let positionToSubmit;
    let relativePositionToUse;

    if (selectedPositionRelative) {
      // If we have a relative position, convert it to absolute pixel coordinates for API
      // Get map dimensions from mapData
      const mapWidth = mapData[mapToSubmit]?.width || 100;
      const mapHeight = mapData[mapToSubmit]?.height || 100;

      // Convert relative coordinates (0-1 range) to absolute pixel coordinates
      positionToSubmit = {
        x: selectedPositionRelative.x * mapWidth,
        y: selectedPositionRelative.y * mapHeight
      };
      relativePositionToUse = selectedPositionRelative;
    } else if (selectedPosition) {
      // Fallback case: we have an absolute position but no relative position
      // Calculate a relative position from the absolute position
      const containerRef = selectedMapContainerRef.current || modalMapContainerRef.current;

      if (containerRef) {
        const containerRect = containerRef.getBoundingClientRect();
        const mapWidth = mapData[mapToSubmit]?.width || 100;
        const mapHeight = mapData[mapToSubmit]?.height || 100;

        // Calculate aspect ratios
        const imageAspectRatio = mapWidth / mapHeight;
        const containerAspectRatio = containerRect.width / containerRect.height;

        // Calculate actual image dimensions and offsets
        let actualImageWidth, actualImageHeight;
        let offsetX = 0, offsetY = 0;

        if (imageAspectRatio > containerAspectRatio) {
          // Image is wider than container (letterboxed)
          actualImageWidth = containerRect.width;
          actualImageHeight = containerRect.width / imageAspectRatio;
          offsetY = (containerRect.height - actualImageHeight) / 2;
        } else {
          // Image is taller than container (pillarboxed)
          actualImageHeight = containerRect.height;
          actualImageWidth = containerRect.height * imageAspectRatio;
          offsetX = (containerRect.width - actualImageWidth) / 2;
        }

        // Calculate relative position from absolute position
        const relativePos = {
          x: (selectedPosition.x - offsetX) / actualImageWidth,
          y: (selectedPosition.y - offsetY) / actualImageHeight
        };

        // Ensure values are within 0-1 range
        relativePos.x = Math.max(0, Math.min(1, relativePos.x));
        relativePos.y = Math.max(0, Math.min(1, relativePos.y));

        // Store the calculated relative position
        setSelectedPositionRelative(relativePos);
        relativePositionToUse = relativePos;

        // Convert to absolute pixel coordinates for API
        // Reuse the mapWidth and mapHeight variables declared above
        // No need to redeclare them

        positionToSubmit = {
          x: relativePos.x * mapWidth,
          y: relativePos.y * mapHeight
        };
      } else {
        // If we can't calculate a relative position, use a default position
        const defaultRelativePos = { x: 0.5, y: 0.5 }; // Center in 0-1 range

        // Get map dimensions from mapData
        const mapWidth = mapData[mapToSubmit]?.width || 100;
        const mapHeight = mapData[mapToSubmit]?.height || 100;

        // Convert relative coordinates (0-1 range) to absolute pixel coordinates
        positionToSubmit = { 
          x: defaultRelativePos.x * mapWidth, 
          y: defaultRelativePos.y * mapHeight 
        };
        console.warn('Using default position (no container ref)');
      }
    } else {
      // If no selection, use a default position
      mapToSubmit = Object.keys(mapData)[0] || '';
      relativePositionToUse = { x: 0.5, y: 0.5 }; // Center in 0-1 range

      // Get map dimensions from mapData
      const mapWidth = mapData[mapToSubmit]?.width || 100;
      const mapHeight = mapData[mapToSubmit]?.height || 100;

      // Convert relative coordinates (0-1 range) to absolute pixel coordinates
      positionToSubmit = { 
        x: relativePositionToUse.x * mapWidth, 
        y: relativePositionToUse.y * mapHeight 
      };

      // Also update the state for UI consistency
      setSelectedMap(mapToSubmit);
      setSelectedPosition({ 
        x: relativePositionToUse.x * 100, 
        y: relativePositionToUse.y * 100 
      });
      setSelectedPositionRelative(relativePositionToUse);
    }

    try {
      // Make sure we're sending valid values to the API
      if (!gameState.challengeId || !mapToSubmit || !positionToSubmit || !positionToSubmit.x || !positionToSubmit.y) {
        console.error('Invalid data for submission:', { 
          gameId: gameState.challengeId, 
          map: mapToSubmit, 
          position: positionToSubmit 
        });
        showError(t('game.errorSubmittingPosition', 'Error submitting position. Missing required data.'));
        return;
      }

      const result = await submitPosition(
        gameState.challengeId,
        mapToSubmit,
        positionToSubmit,
        gameState.sessionId,
        timeRemaining
      );

      // Check if time expired
      if (result.timeExpired) {
        // Create a result object with 0 points
        const timeExpiredResult = {
          score: 0,
          distance: -1, // -1 to hide the line and actual position
          timeExpired: true
        };

        // Update scores in game state with 0 points
        const updatedScores = [...gameState.scores, 0];
        const stateUpdate = { scores: updatedScores };

        updateGameState(stateUpdate);

        // Set time expired flag
        setTimeExpired(true);

        // Set round result and show results
        setRoundResult(timeExpiredResult);
        setShowResults(true);
        return;
      }

      // Update scores in game state
      const updatedScores = [...gameState.scores, result.score];
      const stateUpdate = { scores: updatedScores };

      updateGameState(stateUpdate);

      // Set round result and show results in one render cycle
      setRoundResult(result);
      setShowResults(true);

      // Trigger confetti for high scores
      triggerConfetti(result.score);
    } catch (error) {
      console.error('Error submitting position:', error);
      showError(t('game.errorSubmittingPosition', 'Error submitting position. Please try again.'));
    }
  }, [
    timerRef,
    mapData,
    selectedMap,
    selectedPosition,
    selectedPositionRelative,
    selectedMapContainerRef,
    modalMapContainerRef,
    gameState,
    t,
    setMapData,
    setSelectedMap,
    setSelectedPosition,
    triggerConfetti, 
    setSelectedPositionRelative, 
    setRoundResult, 
    setShowResults, 
    setTimeExpired, 
    updateGameState
  ]);
  */

  // Total rounds: daily challenge uses its own round count, normal game uses 5
  const totalRounds = gameState.dailyRoundCount || 5;

  // Handle next round button click
  const handleNextRound = async () => {
    // Check if this is the final round FIRST before resetting anything
    if (gameState.currentRound >= totalRounds) {
      // Game is over, finalize session - don't reset challenge state
      finalizeSession();
      return;
    }

    // Clear challenge cache to ensure a fresh challenge is loaded for the next round
    clearApiCache('challenge');

    setShowResults(false);
    setSelectedMap('');
    setSelectedPosition(null);
    setSelectedPositionRelative(null);
    setRoundResult(null);
    setTimeRemaining(60);
    setTimeExpired(false);

    // Reset zoom and pan
    resetZoomAndPan();

    // Reset challengeLoaded flag to ensure a new challenge is loaded for the next round
    loadingDispatch({ type: 'SET_CHALLENGE_LOADED', payload: false });

    // Reset challengeId to ensure a new challenge is loaded for the next round
    updateGameState({ challengeId: null });

    // Check if map data is available
    if (mapKeys.length === 0) {
      console.warn('No map data available for next round, attempting to load map data');
      // Try to load map data again
      try {
        const maps = await fetchMapData();
        setMapData(maps);

        // Only proceed if we successfully loaded map data
        if (Object.keys(maps).length === 0) {
          console.error('Failed to load map data for next round');
          onGameEnd();
          return;
        }
      } catch (error) {
        console.error('Error loading map data for next round:', error);
        // If we can't load map data, return to main screen
        handleGameEnd();
        return;
      }
    }

    // Start next round
    updateGameState({ currentRound: gameState.currentRound + 1 });
  };

  // Finalize game session
  const finalizeSession = async () => {
    try {
      // Daily challenge: submit to daily endpoint (scores persisted in DB after each round)
      if (gameState.dailyChallengeId) {
        const res = await fetch('/api/challenges/daily/submit', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            challengeId: gameState.dailyChallengeId
          })
        });

        if (res.ok) {
          const result = await res.json();
          setRoundResult({
            isFinal: true,
            totalScore: result.totalScore || gameState.scores.reduce((a, b) => a + b, 0),
            rank: result.rank,
            isDailyChallenge: true,
            newAchievements: result.newAchievements || []
          });
        } else {
          setRoundResult({
            isFinal: true,
            totalScore: gameState.scores.reduce((a, b) => a + b, 0),
            isDailyChallenge: true,
            newAchievements: []
          });
        }

        modalDispatch({ type: 'SHOW_FINAL_SCORE' });
        return;
      }

      const finalResult = await finalizeGameSession(gameState.sessionId);
      setRoundResult({ ...finalResult, isFinal: true });
      modalDispatch({ type: 'SHOW_FINAL_SCORE' });
    } catch (error) {
      console.error('Error finalizing session:', error);
      handleGameEnd();
    }
  };

  // Format time remaining
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  // Show error modal with a message and optional callback
  const showError = useCallback((message, callback = null) => {
    modalDispatch({ type: 'SHOW_ERROR', payload: { message, callback } });
  }, []);

  // Handle error modal close
  const handleErrorModalClose = useCallback(() => {
    const callback = errorCallback;
    modalDispatch({ type: 'CLOSE_ERROR' });
    if (callback) {
      callback();
    }
  }, [errorCallback]);

  // State for volume dropdown visibility
  const [showVolumeControl, setShowVolumeControl] = useState(false);

  // Toggle session mute state
  const toggleSessionMute = () => {
    if (globalVolume === 0) {
      // If volume is 0, set it to 100 when unmuting
      setGlobalVolume(100);
      setGlobalMuted(false);
    } else {
      // Toggle mute state
      setGlobalMuted(prev => !prev);
    }
  };

  // Handle volume change
  const handleVolumeChange = (e) => {
    const newVolume = parseInt(e.target.value, 10);
    setGlobalVolume(newVolume);

    // If volume is set to 0, mute the audio
    // If volume is changed from 0, unmute the audio
    if (newVolume === 0) {
      setGlobalMuted(true);
    } else if (globalMuted && newVolume > 0) {
      setGlobalMuted(false);
    }
  };

  // Toggle volume control visibility
  const toggleVolumeControl = (e) => {
    e.stopPropagation(); // Prevent the click from triggering the mute toggle
    setShowVolumeControl(prev => !prev);
  };

  // Close volume control when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      // Check if the click is outside the volume control container
      const volumeContainer = document.querySelector('.volume-control-container');
      if (showVolumeControl && volumeContainer && !volumeContainer.contains(e.target)) {
        setShowVolumeControl(false);
      }
    };

    // Add event listener when dropdown is shown
    if (showVolumeControl) {
      document.addEventListener('click', handleClickOutside);
    }

    // Clean up the event listener
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showVolumeControl]);

  // State for keyboard coordinate input (accessibility feature)
  const [keyboardCoords, setKeyboardCoords] = useState({ x: '', y: '' });

  // Handle keyboard coordinate input change
  const handleKeyboardCoordChange = (axis, value) => {
    // Update the keyboard coords state
    setKeyboardCoords(prev => ({
      ...prev,
      [axis]: value
    }));

    // If both coordinates are valid numbers between 0-100, update the position
    const newCoords = {
      ...keyboardCoords,
      [axis]: value
    };

    const xVal = parseFloat(newCoords.x);
    const yVal = parseFloat(newCoords.y);

    if (!isNaN(xVal) && !isNaN(yVal) &&
        xVal >= 0 && xVal <= 100 &&
        yVal >= 0 && yVal <= 100) {
      // Convert percentage (0-100) to relative position (0-1)
      const relativePos = {
        x: xVal / 100,
        y: yVal / 100
      };

      setSelectedPositionRelative(relativePos);

      // Calculate absolute position for display
      const containerRef = showMapModal ? modalMapContainerRef : selectedMapContainerRef;
      const imageRef = showMapModal ? modalMapImageRef : selectedMapImageRef;
      const currentMapId = showMapModal ? tempSelectedMap : selectedMap;
      const absolutePos = relativeToAbsolutePosition(relativePos, containerRef, imageRef, currentMapId);
      setSelectedPosition(absolutePos);
    }
  };

  // Reset keyboard coords when modal opens
  useEffect(() => {
    if (showMapModal) {
      // If there's an existing position, populate the inputs
      if (selectedPositionRelative) {
        setKeyboardCoords({
          x: (selectedPositionRelative.x * 100).toFixed(1),
          y: (selectedPositionRelative.y * 100).toFixed(1)
        });
      } else {
        setKeyboardCoords({ x: '', y: '' });
      }
    }
  }, [showMapModal, selectedPositionRelative]);

  return (
    <div className="game-screen" role="main" aria-label={t('game.gameScreen', 'Game Screen')}>
      <header className="game-header" role="banner">
        <div className="round-info" aria-label={t('game.roundInfo', 'Round information')}>
          <span className="round-label">{t('game.round')}</span>
          <span className="round-number" aria-current="step">{gameState.currentRound}</span>
          <span className="round-total">/ {totalRounds}</span>
        </div>

        <div className="timer" role="timer" aria-live="polite" aria-label={t('game.timeRemaining', 'Time remaining')}>
          <i className="fas fa-clock" aria-hidden="true"></i>
          <span className="time-remaining">{formatTime(timeRemaining)}</span>
        </div>

        <div className="score-display" aria-label={t('game.currentScore', 'Current score')}>
          <span className="score-label">{t('game.score')}</span>
          <span className="score-value" aria-live="polite">
            {totalScore}
          </span>
        </div>

        <button
          className="report-btn"
          onClick={() => setShowReportModal(true)}
          aria-label={t('report.button', 'Report an issue')}
          title={t('report.button', 'Report an issue')}
        >
          <i className="fas fa-flag" aria-hidden="true"></i>
        </button>
      </header>

      <div className={`game-content ${showResults ? 'results-shown' : ''} ${showResults && roundResult?.isFinal ? 'final-state' : ''} ${gameLayout === 'split' ? 'game-content-split' : ''}`}>
        {loading ? (
          <div className="loading-container" role="status" aria-busy="true" aria-live="polite">
            <div className="loading-spinner" aria-hidden="true"></div>
            <p>{t('game.loading')}</p>
          </div>
        ) : (
          <>
            {showResults && (
              <div className="results-container top-results">
                <div className="result-details">
                  <h3>{t('game.roundResult')}</h3>
                  <div className="result-stats">
                    <div className="result-stat">
                      <span className="stat-label">{t('game.selectedMap')}</span>
                      <span className="stat-value">{mapData[selectedMap]?.name || selectedMap}</span>
                    </div>
                    <div className="result-stat">
                      <span className="stat-label">{t('game.score')}</span>
                      <span className="stat-value">{roundResult?.score || 0}</span>
                    </div>
                    <div className="result-stat">
                      <span className="stat-label">{t('game.distance')}</span>
                      <span className="stat-value">
                        {roundResult?.distance !== -1 ? `${roundResult?.distance || 0} ${t('game.pixels')}` : ''}
                      </span>
                    </div>
                    <div className="result-stat">
                      <span className="stat-label">{t('game.currentRound')}</span>
                      <span className="stat-value">{gameState.currentRound} / {totalRounds}</span>
                    </div>
                    <div className="result-stat">
                      <span className="stat-label">{t('game.totalScore')}</span>
                      <span className="stat-value">
                        {totalScore}
                      </span>
                    </div>
                  </div>
                  <p className="location-description">
                    {timeExpired 
                      ? t('game.timeExpiredScore', 'Time expired. Your score for this round is 0 points.')
                      : (roundResult?.actualPosition?.description || t('game.unknownLocation'))}
                  </p>
                  {!roundResult?.isFinal && (
                    <button
                      className="btn btn-primary next-round-btn"
                      onClick={handleNextRound}
                    >
                      {t('game.nextRoundButton')}
                    </button>
                  )}
                </div>
                <Advertisement zone="results-screen" className="game-results-ad" />
              </div>
            )}
            <div className="challenge-container">
              {!showResults && (
                <div className="challenge-image-container">
                  <img 
                    src={gameState.challengeImage} 
                    alt={t('game.challengeImage')} 
                    className="challenge-image" 
                  />
                </div>
              )}

              <div className="map-selection">
                {!selectedMap || (selectedMap && !showResults) || (showResults && timeExpired) ? (
                  <>
                    <h3 id="map-selection-heading">{t('game.selectMap')}</h3>
                    <div className="map-buttons" role="group" aria-labelledby="map-selection-heading">
                      {mapKeys.map(mapId => (
                        <button
                          key={mapId}
                          className={`map-button ${selectedMap === mapId ? 'selected' : ''}`}
                          onClick={() => handleMapSelect(mapId)}
                          disabled={showResults && timeExpired}
                          aria-pressed={selectedMap === mapId}
                          aria-label={t('game.selectMapNamed', { map: mapData[mapId]?.name || mapId }, `Select ${mapData[mapId]?.name || mapId}`)}
                        >
                          {mapData[mapId]?.name || mapId}
                        </button>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="selected-map-result">
                    <div
                      className={`selected-map-container ${isMapFocused ? 'map-focused' : ''}`}
                      ref={selectedMapContainerRef}
                      onClick={!showResults ? handleMapClick : undefined}
                      onMouseDown={handleMouseDown}
                      onMouseMove={handleMouseMove}
                      onMouseUp={handleMouseUp}
                      onMouseLeave={handleMouseUp}
                      onContextMenu={(e) => e.preventDefault()}
                      style={{ cursor: zoomLevel > 1 ? 'grab' : 'crosshair' }}
                    >
                      <div className="zoom-controls" role="group" aria-label={t('game.zoomControls', 'Zoom controls')}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            zoomDispatch({ type: 'SET_ZOOM', payload: Math.min(5, zoomLevel + 0.1) });
                          }}
                          aria-label={t('game.zoomIn', 'Zoom in')}
                        >+</button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            zoomDispatch({ type: 'SET_ZOOM', payload: Math.max(1, zoomLevel - 0.1) });
                          }}
                          aria-label={t('game.zoomOut', 'Zoom out')}
                        >-</button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            resetZoomAndPan();
                          }}
                          aria-label={t('game.resetZoom', 'Reset zoom')}
                        >{t('game.resetZoom', 'Reset')}</button>
                      </div>
                      <img
                        src={`/images/maps/${selectedMap}.webp`}
                        alt={mapData[selectedMap]?.name || selectedMap}
                        className="selected-map-image"
                        ref={selectedMapImageRef}
                        loading="lazy"
                        style={{
                          transform: `scale(${zoomLevel}) translate(${panPosition.x / zoomLevel}px, ${panPosition.y / zoomLevel}px)`,
                          transformOrigin: 'center',
                          transition: isDragging ? 'none' : 'transform 0.1s ease-out'
                        }}
                      />
                      <div 
                        className="map-overlay" 
                        ref={selectedMapOverlayRef}
                        style={{
                          transform: `scale(${zoomLevel}) translate(${panPosition.x / zoomLevel}px, ${panPosition.y / zoomLevel}px)`,
                          transformOrigin: 'center',
                          transition: isDragging ? 'none' : 'transform 0.1s ease-out'
                        }}
                      >
                        {selectedPosition && selectedPositionRelative && (
                          <div 
                            className="position-marker small"
                            style={{
                              position: 'absolute',
                              left: `${selectedPosition.x}px`,
                              top: `${selectedPosition.y}px`,
                              zIndex: '10',
                              pointerEvents: 'none',
                              transform: 'translate(-50%, -50%)'
                            }}
                          ></div>
                        )}
                        {/* Display actual position as blue point if user selected the correct map */}
                        {showResults && roundResult && roundResult.distance !== -1 && roundResult.actualPosition && (
                          <>
                            {/* Draw line between user's guess and actual position */}
                            {selectedPosition && selectedPositionRelative && (
                              <svg 
                                style={{
                                  position: 'absolute',
                                  top: 0,
                                  left: 0,
                                  width: '100%',
                                  height: '100%',
                                  zIndex: '9',
                                  pointerEvents: 'none'
                                }}
                              >
                                <line 
                                  x1={selectedPosition.x}
                                  y1={selectedPosition.y}
                                  x2={relativeToAbsolutePosition({x: roundResult.actualPosition.x / mapData[selectedMap]?.width, y: roundResult.actualPosition.y / mapData[selectedMap]?.height}, selectedMapContainerRef, selectedMapImageRef)?.x}
                                  y2={relativeToAbsolutePosition({x: roundResult.actualPosition.x / mapData[selectedMap]?.width, y: roundResult.actualPosition.y / mapData[selectedMap]?.height}, selectedMapContainerRef, selectedMapImageRef)?.y}
                                  style={{
                                    stroke: '#ffffff',
                                    strokeWidth: '2px',
                                    strokeDasharray: '5,5'
                                  }}
                                />
                              </svg>
                            )}
                            <div 
                              className="position-marker small actual"
                              style={{
                                position: 'absolute',
                                left: `${relativeToAbsolutePosition({x: roundResult.actualPosition.x / mapData[selectedMap]?.width, y: roundResult.actualPosition.y / mapData[selectedMap]?.height}, selectedMapContainerRef, selectedMapImageRef)?.x}px`,
                                top: `${relativeToAbsolutePosition({x: roundResult.actualPosition.x / mapData[selectedMap]?.width, y: roundResult.actualPosition.y / mapData[selectedMap]?.height}, selectedMapContainerRef, selectedMapImageRef)?.y}px`,
                                zIndex: '11',
                                pointerEvents: 'none',
                                transform: 'translate(-50%, -50%)'
                              }}
                            ></div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      <div className="game-controls">
        {showResults && roundResult?.isFinal && !showFinalScoreModal && (
          <button 
            className="btn btn-primary play-again-btn"
            onClick={handleGameEnd}
          >
            {t('game.playAgainButton')}
          </button>
        )}
      </div>

      {/* Map Selection Modal */}
      {showMapModal && (
        <div className="map-modal-overlay" role="presentation">
          <div
            className="map-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="map-modal-title"
            ref={mapModalFocusTrapRef}
            tabIndex={-1}
          >
            <div className="map-modal-header">
              <h3 id="map-modal-title">
                <span>{t('game.selectPosition')}</span>
                <span className="time-in-title" aria-live="polite">{formatTime(timeRemaining)}</span>
              </h3>
              <button
                className="modal-close-btn"
                onClick={handleModalCancel}
                aria-label={t('game.closeModal', 'Close modal')}
              >
                <i className="fas fa-times" aria-hidden="true"></i>
              </button>
            </div>
            <div className="map-modal-content">
              <div
                className={`map-container ${isMapFocused ? 'map-focused' : ''}`}
                ref={modalMapContainerRef}
                onClick={handleMapClick}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
                onContextMenu={(e) => e.preventDefault()}
                style={{ cursor: zoomLevel > 1 ? 'grab' : 'crosshair' }}
                role="application"
                aria-label={t('game.mapInteractiveArea', 'Interactive map area - click to select position')}
              >
                <div className="zoom-controls" role="group" aria-label={t('game.zoomControls', 'Zoom controls')}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      zoomDispatch({ type: 'SET_ZOOM', payload: Math.min(5, zoomLevel + 0.1) });
                    }}
                    aria-label={t('game.zoomIn', 'Zoom in')}
                  >+</button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      zoomDispatch({ type: 'SET_ZOOM', payload: Math.max(1, zoomLevel - 0.1) });
                    }}
                    aria-label={t('game.zoomOut', 'Zoom out')}
                  >-</button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      resetZoomAndPan();
                    }}
                    aria-label={t('game.resetZoom', 'Reset zoom')}
                  >{t('game.resetZoom', 'Reset')}</button>
                </div>
                <img
                  src={`/images/maps/${tempSelectedMap}.webp`}
                  alt={mapData[tempSelectedMap]?.name || tempSelectedMap}
                  className="map-image"
                  ref={modalMapImageRef}
                  loading="lazy"
                  style={{
                    transform: `scale(${zoomLevel}) translate(${panPosition.x / zoomLevel}px, ${panPosition.y / zoomLevel}px)`,
                    transformOrigin: 'center',
                    transition: isDragging ? 'none' : 'transform 0.1s ease-out'
                  }}
                />
                <div 
                  className="map-overlay" 
                  ref={modalMapOverlayRef}
                  style={{
                    transform: `scale(${zoomLevel}) translate(${panPosition.x / zoomLevel}px, ${panPosition.y / zoomLevel}px)`,
                    transformOrigin: 'center',
                    transition: isDragging ? 'none' : 'transform 0.1s ease-out'
                  }}
                >
                  {selectedPosition && selectedPositionRelative && (
                    <div 
                      className="position-marker"
                      style={{
                        position: 'absolute',
                        left: `${selectedPosition.x}px`,
                        top: `${selectedPosition.y}px`,
                        zIndex: '10',
                        pointerEvents: 'none',
                        transform: 'translate(-50%, -50%)'
                      }}
                    ></div>
                  )}
                </div>
              </div>
            </div>
            <div className="map-modal-footer">
              <div className="modal-buttons">
                <button
                  className="btn btn-secondary"
                  onClick={handleModalCancel}
                >
                  {t('game.cancel')}
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleModalConfirm}
                >
                  {t('game.confirm')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Error Modal */}
      {showErrorModal && (
        <div className="map-modal-overlay" role="presentation">
          <div
            className="map-modal"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="error-modal-title"
            aria-describedby="error-modal-message"
            ref={errorModalFocusTrapRef}
            tabIndex={-1}
          >
            <div className="map-modal-header">
              <h3 id="error-modal-title">{t('game.error', 'Error')}</h3>
              <button
                className="modal-close-btn"
                onClick={handleErrorModalClose}
                aria-label={t('game.closeModal', 'Close modal')}
              >
                <i className="fas fa-times" aria-hidden="true"></i>
              </button>
            </div>
            <div className="map-modal-content">
              <p id="error-modal-message" className="error-message" role="alert">{errorMessage}</p>
            </div>
            <div className="map-modal-footer">
              <button
                className="btn btn-primary"
                onClick={handleErrorModalClose}
              >
                {t('game.ok', 'OK')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Final Score Modal */}
      {showFinalScoreModal && (
        <div className="map-modal-overlay" role="presentation">
          <div
            className="map-modal final-score-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="final-score-modal-title"
            ref={finalScoreModalFocusTrapRef}
            tabIndex={-1}
          >
            <div className="map-modal-header">
              <h3 id="final-score-modal-title">{t('game.finalScore', 'Final Score')}</h3>
              <button
                className="modal-close-btn"
                onClick={handleGameEnd}
                aria-label={t('game.closeModal', 'Close modal')}
              >
                <i className="fas fa-times" aria-hidden="true"></i>
              </button>
            </div>
            <div className="map-modal-content">
              <div className="final-score-container">
                <div className="final-score">
                  <h4>{t('game.totalScore', 'Total Score')}</h4>
                  <span className="final-score-value">
                    {totalScore}
                  </span>
                  {roundResult?.isDailyChallenge && roundResult?.rank && (
                    <span className="daily-rank-badge">
                      <i className="fas fa-trophy"></i> #{roundResult.rank}
                    </span>
                  )}
                </div>

                <div className="rounds-scores">
                  <h4>{t('game.roundScores', 'Round Scores')}</h4>
                  {gameState.scores.map((score, index) => (
                    <div key={index} className="round-score-item">
                      <div className="round-info">
                        <span className="round-label">{t('game.round')} {index + 1}</span>
                        <span className="round-score">{score}</span>
                      </div>
                      <div className="score-progress-container">
                        <div
                          className="score-progress-bar"
                          style={{
                            width: `${Math.min(100, (score / 5000) * 100)}%`,
                            backgroundColor: `hsl(${Math.min(120, (score / 5000) * 120)}, 100%, 45%)`
                          }}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* New Achievements Section */}
                {roundResult?.newAchievements && roundResult.newAchievements.length > 0 && (
                  <div className="new-achievements-section">
                    <h4>{t('game.newAchievements', 'Achievements Unlocked!')}</h4>
                    <div className="achievements-list">
                      {roundResult.newAchievements.map((achievement, index) => (
                        <div key={index} className={`achievement-item rarity-${achievement.rarity || 'common'}`}>
                          <div className="achievement-icon">
                            <i className={`fas ${achievement.icon || 'fa-trophy'}`} aria-hidden="true"></i>
                          </div>
                          <div className="achievement-info">
                            <span className="achievement-name">
                              {t(achievement.nameKey, achievement.code)}
                            </span>
                            <span className="achievement-description">
                              {t(achievement.descriptionKey, '')}
                            </span>
                          </div>
                          {achievement.points > 0 && (
                            <div className="achievement-points">
                              +{achievement.points}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="map-modal-footer">
              <button
                className="btn btn-primary"
                onClick={handleGameEnd}
              >
                {t('game.playAgain', 'Play Again')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Report Modal */}
      {showReportModal && (
        <ReportModal
          onClose={() => setShowReportModal(false)}
          context={{
            pointId: gameState.currentPointId || null,
            mapId: gameState.currentMapId || null,
            sessionId: gameState.sessionId || null,
            mapName: selectedMap ? (mapData[selectedMap]?.name || selectedMap) : null
          }}
        />
      )}

      {/* Floating mute button with volume control */}
      <div className="volume-control-container" role="group" aria-label={t('game.audioControls', 'Audio controls')}>
        <button
          className={`floating-mute-button ${globalMuted ? 'muted' : ''}`}
          onClick={toggleSessionMute}
          title={globalMuted ? t('game.unmute', 'Unmute for this session') : t('game.mute', 'Mute for this session')}
          aria-pressed={globalMuted}
          aria-label={globalMuted ? t('game.unmute', 'Unmute for this session') : t('game.mute', 'Mute for this session')}
        >
          <i className={`fas ${globalMuted ? 'fa-volume-mute' : globalVolume > 66 ? 'fa-volume-up' : globalVolume > 33 ? 'fa-volume-down' : 'fa-volume-off'}`} aria-hidden="true"></i>
          <i
            className="fas fa-caret-down volume-caret"
            onClick={toggleVolumeControl}
            role="button"
            aria-label={t('game.toggleVolumeControl', 'Toggle volume control')}
            aria-expanded={showVolumeControl}
            aria-hidden="true"
          ></i>
        </button>

        {showVolumeControl && (
          <div className="volume-dropdown" role="group" aria-label={t('game.volumeSlider', 'Volume slider')}>
            <div className="volume-title" id="volume-slider-label">{t('game.volumeControl', 'Volume Control')}</div>
            <input
              type="range"
              min="0"
              max="100"
              value={globalVolume}
              onChange={handleVolumeChange}
              className="volume-slider"
              aria-labelledby="volume-slider-label"
              aria-valuenow={globalVolume}
              aria-valuemin={0}
              aria-valuemax={100}
            />
            <div className="volume-value" aria-live="polite">{globalVolume}</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GameScreen;
